/**
 * @description 埋点用户身份上下文：供 safeTrack / page_view 统一 enrich，支撑监控后台按客户类型查询
 * @reason 代理商（agency=1）通常仍带 userType=1（企业工程商），展示身份必须以 agency 优先，避免误报为工程商
 */

/**
 * @typedef {Object} TrackUserContext
 * @property {boolean} is_logged_in - 是否登录
 * @property {number|null} user_type - 原始 userType：0 SMB / 1 企业工程商 / 2 个体已认证 / 3 个体未认证
 * @property {string} customer_type - 稳定枚举：guest|agency|smb|enterprise_engineer|individual_engineer_certified|individual_engineer_uncertified
 * @property {string} customer_type_label - 中文展示名（代理商优先显示「代理商」）
 * @property {number|null} agency - 是否代理商：1 是 / 0 否 / null 未知
 * @property {boolean} is_agency - 是否代理商
 * @property {number|null} agency_level - 代理商等级（后台配置值，原样透传）
 * @property {string} role - smb|pro|guest（与商城 role getter 对齐；未登录为 guest）
 * @property {string} [base_customer_type] - 代理商时保留底层 userType 对应枚举，便于排查
 * @property {string} [base_customer_type_label] - 代理商时保留底层 userType 中文名
 */

/** @type {Record<number, { type: string, label: string }>} */
const USER_TYPE_MAP = {
  0: { type: 'smb', label: 'SMB' },
  1: { type: 'enterprise_engineer', label: '企业工程商' },
  2: { type: 'individual_engineer_certified', label: '个体工程商(已认证)' },
  3: { type: 'individual_engineer_uncertified', label: '个体工程商(未认证)' },
}

/** @type {TrackUserContext} */
const GUEST_CONTEXT = Object.freeze({
  is_logged_in: false,
  user_type: null,
  customer_type: 'guest',
  customer_type_label: '未登录',
  agency: null,
  is_agency: false,
  agency_level: null,
  role: 'guest',
})

/** @type {TrackUserContext} */
let _userContext = { ...GUEST_CONTEXT }

/**
 * @description 由 userType 解析监控用客户类型枚举与中文名（不含代理商优先逻辑）
 * @param {unknown} userType
 * @returns {{ type: string, label: string, userType: number|null }}
 */
export function resolveCustomerType(userType) {
  if (userType === undefined || userType === null || userType === '') {
    return { type: 'guest', label: '未登录', userType: null }
  }
  const num = Number(userType)
  if (!Number.isFinite(num) || !USER_TYPE_MAP[num]) {
    return { type: 'unknown', label: '未知', userType: Number.isFinite(num) ? num : null }
  }
  return { ...USER_TYPE_MAP[num], userType: num }
}

/**
 * @description 解析代理商等级（仅透传后台值，不在前端写死等级名称）
 * @param {Object} userInfo
 * @returns {number|null}
 */
function resolveAgencyLevel(userInfo) {
  const agencyLevel = userInfo?.agencyLevel
  if (agencyLevel === undefined || agencyLevel === null || agencyLevel === '') {
    return null
  }
  const level = Number(agencyLevel)
  return Number.isFinite(level) ? level : null
}

/**
 * @description 从商城 userInfo 构建埋点身份字段
 * @param {Object|null|undefined} userInfo
 * @returns {TrackUserContext}
 */
export function buildTrackUserContext(userInfo) {
  if (!userInfo || typeof userInfo !== 'object') {
    return { ...GUEST_CONTEXT }
  }

  const resolved = resolveCustomerType(userInfo.userType)
  const agencyRaw = userInfo.agency
  const agency =
    agencyRaw === undefined || agencyRaw === null || agencyRaw === ''
      ? null
      : Number(agencyRaw)
  const isAgency = agency === 1
  const isLoggedIn = true
  const role =
    resolved.userType === 1 || resolved.userType === 2
      ? 'pro'
      : resolved.userType === null
        ? 'guest'
        : 'smb'

  // 代理商身份优先于 userType：agency=1 的用户底层常为企业工程商，监控展示必须报「代理商」
  if (isAgency) {
    return {
      is_logged_in: isLoggedIn,
      user_type: resolved.userType,
      customer_type: 'agency',
      customer_type_label: '代理商',
      agency: 1,
      is_agency: true,
      agency_level: resolveAgencyLevel(userInfo),
      role,
      base_customer_type: resolved.type,
      base_customer_type_label: resolved.label,
    }
  }

  return {
    is_logged_in: isLoggedIn,
    user_type: resolved.userType,
    customer_type: resolved.type,
    customer_type_label: resolved.label,
    agency: Number.isFinite(agency) ? agency : null,
    is_agency: false,
    agency_level: null,
    role,
  }
}

/**
 * @description 同步埋点用户身份（登录 / 资料更新 / 登出时调用）
 * @param {Object|null|undefined} userInfo - 传入 null 清空为未登录
 * @returns {TrackUserContext}
 */
export function setTrackUserContext(userInfo) {
  _userContext = buildTrackUserContext(userInfo)
  return getTrackUserContext()
}

/**
 * @description 读取当前埋点用户身份（浅拷贝，避免外部篡改）
 * @returns {TrackUserContext}
 */
export function getTrackUserContext() {
  return { ..._userContext }
}

/**
 * @description 清空为未登录身份
 * @returns {void}
 */
export function clearTrackUserContext() {
  _userContext = { ...GUEST_CONTEXT }
}
